export default class RepositoryBase {

    private static readonly development_ip: string = "localhost";
    private static readonly development_port: number = 5164;
    private static readonly development_protocol: string = "https";

    private static readonly production_ip: string = "tipcounter.be";
    private static readonly production_port: number = 443;
    private static readonly production_protocol: string = "https";

    protected static ip: string = process.env.NODE_ENV === "development" ? this.development_ip : this.production_ip;
    protected static port: number = process.env.NODE_ENV === "development" ? this.development_port : this.production_port;
    protected static protocol: string = process.env.NODE_ENV === "development" ? this.development_protocol : this.production_protocol;

    // Error messages

    protected static unexpectedError<T>(): ResponseEnvelope<T> {
        return new ResponseEnvelope<T>(
            false,
            undefined,
            "Failed to retrieve data",
            "An unexpected error occured while trying to fetch data. Please contact an administrator if this error persists"
        );
    }

    protected static invalidFormat<T>(): ResponseEnvelope<T> {
        return new ResponseEnvelope<T>(
            false,
            undefined,
            "Failed to retrieve data",
            "The received data contains invalid JSON. Please contact an administrator if this error persists"
        );
    }

    protected static unableToSendRequest<T>(): ResponseEnvelope<T> {
        return new ResponseEnvelope<T>(
            false,
            undefined,
            "Failed to retrieve data",
            "Unable to send request. Please contact an administrator if this error persists"
        );
    }

    protected static invalidResponse<T>(): ResponseEnvelope<T> {
        return new ResponseEnvelope<T>(
            false,
            undefined,
            "Received invalid data",
            "The received data is not valid. Please contact an administrator if this error persists"
        );
    }

    protected static requestUnsuccessful<T>(): ResponseEnvelope<T> {
        return new ResponseEnvelope<T>(
            false,
            undefined,
            "Request unsuccessful",
            "The request could not be processed correctly."
        );
    }

    private static _csrf: string | null = null;
    protected static async getCsrf(force?: boolean): Promise<ResponseEnvelope<string>> {
        if (!force && this._csrf !== null) return new ResponseEnvelope(true, this._csrf);

        const res = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/auth/csrf`, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
        });

        if (!res.ok) return new ResponseEnvelope(false, "", "Failed to fetch CSRF token", "Failed to fetch CSRF token");
        const data = await res.json();

        this._csrf = data.token;
        if (this._csrf === null) return new ResponseEnvelope(false, "", "No CSRF token received", "No CSRF token received");

        return new ResponseEnvelope(true, this._csrf);
    }

    protected static clearCsrf() {
        this._csrf = null;
    }

    // Sends a mutating request with the given CSRF token. If the antiforgery
    // token has expired the backend responds 400 "CSRF validation failed."; in
    // that case the token is refreshed once via getCsrf(true) and the request
    // is retried a single time. All other responses are returned unchanged.
    protected static async sendWithCsrf(url: string, init: RequestInit, token: string): Promise<Response> {
        const withToken = (t: string): RequestInit => ({
            ...init,
            headers: { ...(init.headers as Record<string, string> | undefined), 'X-CSRF-Token': t },
        });

        let response = await fetch(url, withToken(token));

        if (response.status === 400) {
            const body = await response.clone().text().catch(() => "");
            if (body.includes("CSRF")) {
                const refreshed = await this.getCsrf(true);
                if (refreshed.isSuccess() && refreshed.result) {
                    response = await fetch(url, withToken(refreshed.result));
                }
            }
        }

        return response;
    }
}

export class ResponseEnvelope<T> {
    public success: boolean;
    public result?: T;

    public errorTitle?: string;
    public errorMessage?: string;

    constructor(success: boolean, result?: T, errorTitle?: string, errorMessage?: string) {
        this.success = success;
        this.result = result;
        this.errorTitle = errorTitle;
        this.errorMessage = errorMessage;
    }

    public isFailed(): boolean {
        return !this.success;
    }

    public isSuccess(): boolean {
        return this.success;
    }
}